import { Text, Tab, TabGroup, Divider, Spinner } from '@nike/eds';
import { Exception, Message } from '../../graphql/graphql';
import './exception-detail.css';
import { formatDateTime } from '../../shared/Utils';
import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_EXCEPTION_AUDIT_TRAIL } from '../../graphql/exceptions.graphql';
import { SimplePagination } from '../../shared/SimplePagination';

const ExceptionAuditTrailItems = ({ exceptionId }: { exceptionId: string }) => {
  const [currentOffset, setCurrentOffset] = useState(0);
  const pageSize = 10;

  const { data, loading, error } = useQuery(GET_EXCEPTION_AUDIT_TRAIL, {
    variables: {
      id: exceptionId,
      limit: pageSize,
      offset: currentOffset,
    },
    fetchPolicy: "cache-and-network",
  });

  const handlePageChange = (page: number, offset: number) => {
    setCurrentOffset(offset);
  };

  if (error) return <div>Error loading audit trail: {error.message}</div>;

  const auditTrailData = data?.exception?.get?.auditTrail;
  const items = auditTrailData?.items || [];
  const totalCount = auditTrailData?.pageInfo?.totalCount || 0;

  // Sort by timestamp descending (most recent first)
  const sortedItems = [...items].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <div style={{position: 'relative', minHeight: '220px'}}>
      <div className='exception-audit-trail-items'>
        <ul>
          {sortedItems.map((item) => (
            <li key={item.id}>
              <Text font="body-3" className="eds-spacing--pt-8 eds-spacing--pr-16 eds-spacing--pb-8 eds-spacing--pl-4">
                <strong>{formatDateTime(item.timestamp)}</strong>
                {" - "}
                <b>{item.user?.name || 'System'}</b>
                {" "}
                {item.content
                  ? item.content
                  : item.toStatus && !item.fromStatus 
                    ? <>New Exception raised</>
                    : <>changed status from <b>{item.fromStatus ? item.fromStatus.replace(/_/g, " ") : 'N/A'}</b> to <b>{item.toStatus ? item.toStatus.replace(/_/g, " ") : 'N/A'}</b></>
                }
                {item.targetUser && (
                  <> <b>{item.targetUser.name}</b></>
                )}
              </Text>
              <Divider className="" />
            </li>
          ))}
        </ul>
        {totalCount > pageSize && (
          <SimplePagination
            currentOffset={currentOffset}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            // className="eds-spacing--mt-16"
          />
        )}
      </div>
      {loading && <div style={{position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, background: 'transparent'}} className="eds-flex eds-flex--align-items-center eds-flex--justify-content-center"><Spinner className="" size="large" /></div>}
    </div>
  );
};

const ExceptionMessagesSection = ({ messages }: { messages: Message[] }) => {
  return (
    <div className='exception-messages-section'>
      <ul>
        {messages.map((msg) => (
          <li key={msg.id} >
            <Text font="body-3" className="eds-spacing--pt-8 eds-spacing--pr-16 eds-spacing--pb-8 eds-spacing--pl-4">
              {msg.summary}
            </Text>
            <Divider />
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AuditComments = ({ exception }: { exception: Exception }) => {
  const [activeTab, setActiveTab] = useState("messages");
  const messageEntries = exception.messages as Message[] || [];

  // Get audit trail count from the exception data (for tab label)
  const auditTrailCount = exception.auditTrail?.pageInfo?.totalCount || 0;

  return (
    <>  
      <TabGroup
        name={`exception-detail-tabs-${exception.alias}`} 
        variant='toggle-small'
        activeId={activeTab}
        onChange={e => setActiveTab(e.target.id)}
      >
        <Tab id="messages">Details ({messageEntries.length})</Tab>
        <Tab id="auditTrail">Audit Trail ({auditTrailCount})</Tab>
      </TabGroup>

      <div>
        {activeTab === "messages" && (
          <ExceptionMessagesSection messages={messageEntries} />
        )}
        {activeTab === "auditTrail" && (
          <ExceptionAuditTrailItems exceptionId={exception.id} />
        )}
      </div>
    </>
  );
};